/**
 * KEYNOTE — shared "add to bag" behaviour for the custom homepage sections
 * (Featured Picks / Women's / Bottoms / Best Sellers).
 *
 * Works with snippets/keynote-add-to-bag.liquid. Uses event delegation on
 * `document`, so it only needs to be loaded once no matter how many product
 * cards render the control.
 *
 * Flow
 *  1. Tap "+ ADD TO BAG" on a card            → the size pop-up (<dialog data-katb-sheet>) opens.
 *  2. Tap a size in the pop-up                → that variant is added to the cart,
 *                                               the pop-up closes, the cart drawer opens.
 *  Products with a single variant skip the pop-up: the button adds straight away
 *  and opens the cart.
 *
 * After adding we ask the Ajax Cart API for the cart drawer's section HTML
 * (same trick as assets/product-form.js) and hand it to the theme through the
 * standard `cart:update` event, so the drawer + header bubble refresh instantly.
 */
(function () {
  if (window.__keynoteAddToBagBound) return;
  window.__keynoteAddToBagBound = true;

  const routes = (window.Theme && window.Theme.routes) || {};
  const CART_ADD_URL = routes.cart_add_url || '/cart/add.js';
  const CART_URL = '/cart.js';
  const CART_PAGE_URL = routes.cart_url || '/cart';

  /* ------------------------------------------------------------------
   * Scroll lock while the pop-up is open
   * ------------------------------------------------------------------ */
  let prevOverflow = null;

  function lockScroll() {
    if (prevOverflow !== null) return;
    prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (prevOverflow === null) return;
    document.documentElement.style.overflow = prevOverflow;
    prevOverflow = null;
  }

  /* ------------------------------------------------------------------
   * Pop-up open / close
   * ------------------------------------------------------------------ */
  function openSheet(sheet) {
    if (!sheet || sheet.open) return;

    resetSheet(sheet);
    lockScroll();

    if (typeof sheet.showModal === 'function') {
      sheet.showModal();
    } else {
      // very old browsers: show as a plain fixed overlay
      sheet.classList.add('is-fallback');
      sheet.setAttribute('open', '');
    }
  }

  function closeSheet(sheet) {
    if (!sheet) return;

    if (typeof sheet.close === 'function' && sheet.open) {
      sheet.close(); // fires the 'close' event → unlockScroll below
    } else {
      sheet.removeAttribute('open');
    }
    unlockScroll();
  }

  function resetSheet(sheet) {
    delete sheet.dataset.busy;
    const msg = sheet.querySelector('[data-katb-msg]');
    if (msg) msg.textContent = '';
    sheet.querySelectorAll('[data-katb-option]').forEach((el) => {
      el.classList.remove('is-selected', 'is-loading');
      el.removeAttribute('aria-busy');
    });
  }

  // <dialog> 'close' doesn't bubble → listen in the capture phase.
  // Also covers the Esc key.
  document.addEventListener(
    'close',
    (event) => {
      if (event.target && event.target.matches && event.target.matches('[data-katb-sheet]')) {
        unlockScroll();
      }
    },
    true
  );

  /* ------------------------------------------------------------------
   * Cart helpers
   * ------------------------------------------------------------------ */
  function cartSectionIds() {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  /**
   * Adds one unit of a variant, then tells the theme the cart changed.
   * Throws an Error with a shopper-friendly message on failure.
   */
  async function addToCart(variantId, source) {
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    const sectionIds = cartSectionIds();
    if (sectionIds.length) body.append('sections', sectionIds.join(','));

    const response = await fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Sorry, we couldn\'t add that. Please try again.');
    }

    const cartResponse = await fetch(CART_URL, { headers: { Accept: 'application/json' } });
    const cart = await cartResponse.json();

    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: String(variantId),
          data: {
            itemCount: cart.item_count,
            source: source || 'keynote-add-to-bag',
            variantId: String(variantId),
            sections: data.sections
          }
        }
      })
    );
  }

  /** Opens the cart drawer; falls back to the cart page if there is no drawer. */
  async function openCart() {
    const drawer = document.querySelector('cart-drawer-component');

    if (!drawer) {
      window.location.href = CART_PAGE_URL;
      return;
    }

    try {
      await Promise.race([
        customElements.whenDefined('cart-drawer-component'),
        new Promise((_, reject) => setTimeout(reject, 2500))
      ]);
      drawer.open();
    } catch (error) {
      window.location.href = CART_PAGE_URL;
    }
  }

  /* ------------------------------------------------------------------
   * Clicks: open pop-up, close pop-up, choose an option
   * ------------------------------------------------------------------ */
  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // 1) "+ ADD TO BAG" on the card → open the pop-up
    const openButton = target.closest('[data-katb-open]');
    if (openButton) {
      const wrapper = openButton.closest('[data-katb]');
      openSheet(wrapper && wrapper.querySelector('[data-katb-sheet]'));
      return;
    }

    // 2) × button in the pop-up
    const closeButton = target.closest('[data-katb-close]');
    if (closeButton) {
      closeSheet(closeButton.closest('[data-katb-sheet]'));
      return;
    }

    // 3) tap on the dimmed area outside the panel
    if (target.matches('[data-katb-sheet]')) {
      closeSheet(target);
      return;
    }

    // 4) tap on a size / option → add to bag → open cart
    const option = target.closest('[data-katb-option]');
    if (!option || option.disabled || option.dataset.unavailable === 'true') return;

    const sheet = option.closest('[data-katb-sheet]');
    const wrapper = option.closest('[data-katb]');
    if (!sheet || !wrapper || sheet.dataset.busy) return;

    sheet.dataset.busy = 'true';
    const msg = sheet.querySelector('[data-katb-msg]');
    if (msg) msg.textContent = '';

    // Safety net: a value can render with no matching variant (deleted
    // variant, combined-listing mismatch, etc). Liquid then outputs an
    // empty data-variant-id, which Shopify's /cart/add.js rejects with a
    // generic "The string did not match the expected pattern." Catch it
    // here with a clearer message instead of hitting the network.
    if (!option.dataset.variantId) {
      delete sheet.dataset.busy;
      if (msg) msg.textContent = 'This option isn\'t available right now.';
      return;
    }

    sheet.querySelectorAll('[data-katb-option]').forEach((el) => {
      el.classList.toggle('is-selected', el === option);
    });
    option.classList.add('is-loading');
    option.setAttribute('aria-busy', 'true');

    try {
      await addToCart(option.dataset.variantId, wrapper.dataset.source);
      closeSheet(sheet);
      resetSheet(sheet);
      openCart();
    } catch (error) {
      console.error(error);
      option.classList.remove('is-selected', 'is-loading');
      option.removeAttribute('aria-busy');
      delete sheet.dataset.busy;
      if (msg) msg.textContent = error.message || 'Sorry, we couldn\'t add that. Please try again.';
    }
  });

  /* ------------------------------------------------------------------
   * Single-variant products (no pop-up): add straight away, then open cart
   * ------------------------------------------------------------------ */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest ? event.target.closest('[data-katb-form]') : null;
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[data-katb-submit]');
    const label = form.querySelector('[data-katb-label]');
    if (!button || button.disabled) return;

    const wrapper = form.closest('[data-katb]');
    const original = label ? label.textContent : '+ ADD TO BAG';
    button.disabled = true;
    if (label) label.textContent = 'ADDING…';

    try {
      const variantId = new FormData(form).get('id');
      await addToCart(variantId, wrapper && wrapper.dataset.source);

      if (label) label.textContent = '✓ ADDED';
      openCart();
      setTimeout(() => {
        if (label) label.textContent = original;
        button.disabled = false;
      }, 1100);
    } catch (error) {
      console.error(error);
      if (label) label.textContent = 'TRY AGAIN';
      setTimeout(() => {
        if (label) label.textContent = original;
        button.disabled = false;
      }, 1400);
    }
  });
})();
